import { useId, useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  effectiveHex,
  hexToHsl,
  hslToHex,
  wheelPick,
  wheelPosition,
  type ColourState,
  type Hsl,
} from '../../shared/colour';
import './huisstijl.css';

/**
 * Eén kleur van de huisstijl (I1): kleurenrad, hexcode en helderheid, die elkaar live volgen.
 *
 * - Het rad: de hoek is de tint, de afstand tot het midden de verzadiging. Slepen of klikken; met
 *   het toetsenbord draaien links/rechts de tint en op/neer de verzadiging (Shift: grotere stap).
 * - De hexcode: typen zet het rad op die kleur zodra de code klopt; draaien vult de code in.
 * - De helderheid: de schuif van zwart via de kleur naar wit.
 *
 * Het rad en de schuif zijn `role="slider"`, met de hexcode als `aria-valuetext`: wie niet ziet
 * waar de stip staat, hoort welke kleur het is.
 */
export function ColourPicker({
  title,
  hint,
  state,
  onChange,
  placeholder,
  children,
}: {
  title: string;
  hint: string;
  state: ColourState;
  onChange: (next: ColourState) => void;
  /** Wat er in een leeg veld staat: de kleur van UP2D8 als het bedrijf niets koos. */
  placeholder?: string;
  /** Onder de kleur: de contrastwaarschuwing. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const wheel = useRef<HTMLDivElement>(null);
  const lum = useRef<HTMLDivElement>(null);

  const hex = effectiveHex(state);
  const { hsl } = state;
  const dot = wheelPosition(hsl);

  // Draaien of schuiven vult de code in; de tint blijft in `hsl`, ook bij wit of zwart.
  const setHsl = (next: Hsl) => onChange({ text: hslToHex(next), hsl: next });

  const pickWheel = (event: PointerEvent<HTMLDivElement>) => {
    const box = wheel.current?.getBoundingClientRect();
    if (!box) return;
    const { h, s } = wheelPick(
      (event.clientX - box.left) / box.width,
      (event.clientY - box.top) / box.height,
    );
    setHsl({ ...hsl, h, s });
  };

  const pickLum = (event: PointerEvent<HTMLDivElement>) => {
    const box = lum.current?.getBoundingClientRect();
    if (!box) return;
    const l = Math.max(0, Math.min(1, (event.clientX - box.left) / box.width)) * 100;
    setHsl({ ...hsl, l });
  };

  const onWheelKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 15 : 5;
    const next = { ...hsl };
    if (event.key === 'ArrowRight') next.h = (hsl.h + step) % 360;
    else if (event.key === 'ArrowLeft') next.h = (hsl.h - step + 360) % 360;
    else if (event.key === 'ArrowUp') next.s = Math.min(100, hsl.s + step);
    else if (event.key === 'ArrowDown') next.s = Math.max(0, hsl.s - step);
    else return;
    event.preventDefault();
    setHsl(next);
  };

  const onLumKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 10 : 2;
    let l = hsl.l;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') l = Math.min(100, l + step);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') l = Math.max(0, l - step);
    else if (event.key === 'Home') l = 0;
    else if (event.key === 'End') l = 100;
    else return;
    event.preventDefault();
    setHsl({ ...hsl, l });
  };

  // Tijdens het slepen de aanwijzer vasthouden, zodat de stip meegaat buiten het rad.
  const drag = (pick: (event: PointerEvent<HTMLDivElement>) => void) => ({
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.focus();
      pick(event);
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) pick(event);
    },
  });

  return (
    <div className="a-sect">
      <h3 className="a-sect-title" id={`${id}-title`}>
        {title}
      </h3>
      <div className="a-hint" style={{ marginTop: -6 }}>
        {hint}
      </div>
      <div className="a-wheel-wrap">
        <div
          ref={wheel}
          className="a-wheel"
          role="slider"
          tabIndex={0}
          aria-label={t('beheer.huisstijl.wheel', { name: title })}
          aria-valuemin={0}
          aria-valuemax={360}
          aria-valuenow={Math.round(hsl.h)}
          aria-valuetext={hex}
          onKeyDown={onWheelKey}
          {...drag(pickWheel)}
        >
          <span
            className="a-wheel-dot"
            style={{ left: `${dot.left}%`, top: `${dot.top}%`, background: hex }}
          />
        </div>
        <div className="a-stack" style={{ flex: 1, gap: 12 }}>
          <div className="a-field">
            <label className="a-label" id={`${id}-hex-label`} htmlFor={`${id}-hex`}>
              {t('beheer.huisstijl.hex')}
            </label>
            <div className="a-hex">
              <span className="a-swatch" style={{ background: hex }} aria-hidden="true" />
              <input
                id={`${id}-hex`}
                className="a-input"
                aria-labelledby={`${id}-title ${id}-hex-label`}
                aria-describedby={`${id}-hex-hint`}
                value={state.text}
                placeholder={placeholder}
                maxLength={7}
                spellCheck={false}
                autoComplete="off"
                onChange={(event) => {
                  const text = event.target.value;
                  // Een kleur die klopt, zet het rad; een halve code laat het staan.
                  onChange({ text, hsl: hexToHsl(text) ?? hsl });
                }}
              />
            </div>
            <div className="a-hint" id={`${id}-hex-hint`}>
              {t('beheer.huisstijl.hexHint')}
            </div>
          </div>
          <div className="a-field">
            <div className="a-label" id={`${id}-lum-label`}>
              {t('beheer.huisstijl.lightness')}
            </div>
            <div
              ref={lum}
              className="a-lum"
              role="slider"
              tabIndex={0}
              aria-labelledby={`${id}-title ${id}-lum-label`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(hsl.l)}
              aria-valuetext={`${Math.round(hsl.l)}% · ${hex}`}
              style={{
                background: `linear-gradient(90deg, #000, ${hslToHex({ ...hsl, l: 50 })}, #fff)`,
              }}
              onKeyDown={onLumKey}
              {...drag(pickLum)}
            >
              <span style={{ left: `${hsl.l}%`, background: hex }} />
            </div>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
